/**
 * GameState - ゲーム状態の型定義と初期化
 *
 * ゲームロジックに必要な状態をプレーンオブジェクトで管理。
 * React hooksに依存しない純粋なデータ構造。
 */

import {
  INITIAL_LIFE,
  INITIAL_SP,
  INITIAL_HAND_SIZE,
} from '../../utils/constants';
import { statusEffectEngine } from '../statusEffects';
import { calculateSummonCost } from '../summonCost';
import { calculateSummonCostModifierPure } from './continuousEffectEnginePure';
import { createDeepSeaZone } from '../deepSeaZone';
import type { DeepSeaZone } from '../deepSeaZone/types';
import type { CardWithId, MonsterInstance, CardData, PhaseCardInstance, FieldCardInstance, CardType } from '../../types/card';
import type { StatusEffect, TriggerPriorityValue, ActivationTypeValue, TriggerTypeValue } from '../../types/effect';
import type { PlayerNumber } from '../../types/player';

// ========================================
// 定数
// ========================================

export const GAME_STATUS = {
  PLAYING: 'playing',
  GAME_OVER: 'gameOver',
} as const;

export type GameStatusType = typeof GAME_STATUS[keyof typeof GAME_STATUS];

export const PHASES = {
  TURN_START: 0,    // ターン開始
  DRAW: 1,          // ドロー
  MAIN: 2,          // メイン
  BATTLE: 3,        // バトル
  END: 4,           // エンド
} as const;

export type PhaseType = typeof PHASES[keyof typeof PHASES];

export const PHASE_NAMES: readonly string[] = ['ターン開始', 'ドロー', 'メイン', 'バトル', 'エンド'];

// ========================================
// 型定義
// ========================================

/** ログエントリ */
export interface LogEntry {
  message: string;
  type: 'info' | 'damage' | 'heal';
}

/** プレイヤー状態 */
export interface PlayerState {
  life: number;
  deck: CardWithId[];
  hand: CardWithId[];
  field: (MonsterInstance | null)[];
  graveyard: CardWithId[];
  activeSP: number;
  restedSP: number;
  fieldCard: FieldCardInstance | CardData | null;
  phaseCard: PhaseCardInstance | null;
  statusEffects: StatusEffect[];
  nextTurnSPBonus: number;
  magicBlocked: boolean;
  spReduction: number;
  bonusSP: number;
  /** 深海領域（水属性専用メカニクス） */
  deepSeaZone: DeepSeaZone;
}

/** ターンフラグ */
export interface TurnFlags {
  chargeUsedThisTurn: boolean;
  pendingOpponentSPRest: PlayerNumber | null;
}

/** カードプレイ履歴エントリ */
export interface PlayedCardEntry {
  id: string;
  name: string;
  attribute: string;
  cost: number;
  type: string;
  action: string;
}

/** トリガーエントリ */
export interface TriggerEntry {
  cardId: string;
  cardName?: string;
  triggerType?: TriggerTypeValue;
  owner: PlayerNumber;
  slotIndex: number | null;
  priority?: TriggerPriorityValue;
  activationType?: ActivationTypeValue;
  condition?: ((context: unknown) => boolean) | undefined;
  effect?: ((context: unknown) => void) | undefined;
  description?: string;
  usedThisTurn?: boolean;
  targetSelf?: boolean;
  [key: string]: unknown;
}

/** ゲーム設定 */
export interface GameConfig {
  summoningSickness: boolean;
  secondPlayerBonusSP: boolean;
  piercingDamage: boolean;
  loneWarriorRule: boolean;
  initialLifePoints: number;
}

/** 初期状態生成設定 */
export interface CreateInitialStateConfig {
  deck1: CardWithId[];
  deck2: CardWithId[];
  firstPlayer?: PlayerNumber;
  summoningSickness?: boolean;
  secondPlayerBonusSP?: boolean;
  piercingDamage?: boolean;
  loneWarriorRule?: boolean;
  initialLifePoints?: number;
}

/** ゲーム状態 */
export interface GameState {
  config: GameConfig;
  firstPlayer: PlayerNumber;
  turn: number;
  currentPlayer: PlayerNumber;
  phase: PhaseType;
  isFirstTurn: boolean;
  winner: PlayerNumber | null;
  gameStatus: GameStatusType;
  p1: PlayerState;
  p2: PlayerState;
  turnFlags: TurnFlags;
  triggers: Record<string, TriggerEntry[]>;
  logs: LogEntry[];
  playedCardsHistory: {
    p1: PlayedCardEntry[];
    p2: PlayedCardEntry[];
  };
}

// ========================================
// プレイヤー状態の初期化
// ========================================

/**
 * プレイヤー状態を初期化
 */
export function createPlayerState(deck: CardWithId[], initialLife: number = INITIAL_LIFE): PlayerState {
  const hand = deck.slice(0, INITIAL_HAND_SIZE);
  const remainingDeck = deck.slice(INITIAL_HAND_SIZE);

  return {
    life: initialLife,
    deck: remainingDeck,
    hand: hand,
    field: [null, null, null, null, null],
    graveyard: [],
    activeSP: INITIAL_SP,
    restedSP: 0,
    fieldCard: null,
    phaseCard: null,
    statusEffects: [],        // プレイヤー自身の状態異常（毒など）
    nextTurnSPBonus: 0,       // 次ターンSP増加ボーナス
    magicBlocked: false,      // 魔法カード使用制限
    spReduction: 0,           // 次ターンSP増加減少
    bonusSP: 0,               // 後攻ボーナスSP（使うまで残る）
    deepSeaZone: createDeepSeaZone(), // 深海領域（初期状態は未開放）
  };
}

/**
 * ターンフラグを初期化
 */
export function createTurnFlags(): TurnFlags {
  return {
    chargeUsedThisTurn: false,
    // AIの攻撃済みモンスター追跡はUIレイヤーで管理
    // C0000313 アクア・リヴァイアサン: 次の相手ターン開始時にSP1つをレスト状態にする
    // targetPlayer: 効果対象のプレイヤー番号（1 or 2）、null/undefinedなら効果なし
    pendingOpponentSPRest: null,
  };
}

// ========================================
// ゲーム状態の初期化
// ========================================

/**
 * 初期ゲーム状態を生成
 */
export function createInitialState(config: CreateInitialStateConfig): GameState {
  const { deck1, deck2, firstPlayer } = config;
  const initialLife = config.initialLifePoints ?? INITIAL_LIFE;

  // 先攻決定（指定がなければランダム）
  const first: PlayerNumber = firstPlayer ?? (Math.random() < 0.5 ? 1 : 2);

  return {
    // ゲーム設定（召喚酔い等）
    config: {
      summoningSickness: config.summoningSickness ?? true, // デフォルトON
      secondPlayerBonusSP: config.secondPlayerBonusSP ?? true, // 後攻ボーナスSP（デフォルトON）
      piercingDamage: config.piercingDamage ?? true, // 貫通ダメージ（デフォルトON）
      loneWarriorRule: config.loneWarriorRule ?? true, // 孤軍奮闘（デフォルトON）
      initialLifePoints: initialLife, // 初期LP
    },

    // 先攻プレイヤー（後攻ボーナス判定用）
    firstPlayer: first,

    // ゲーム進行
    turn: 1,
    currentPlayer: first,
    phase: PHASES.TURN_START,
    isFirstTurn: true,
    winner: null,
    gameStatus: GAME_STATUS.PLAYING,

    // プレイヤー状態（後攻プレイヤーは初期SP+1）
    p1: {
      ...createPlayerState(deck1, initialLife),
      activeSP: first === 1 ? INITIAL_SP : INITIAL_SP + 1,
    },
    p2: {
      ...createPlayerState(deck2, initialLife),
      activeSP: first === 2 ? INITIAL_SP : INITIAL_SP + 1,
    },

    // ターン内フラグ
    turnFlags: createTurnFlags(),

    // トリガーレジストリ（純粋関数版）
    // Map<triggerType, Array<TriggerEntry>>をオブジェクトで表現
    triggers: {},

    // ログ
    logs: [{ message: `🎲 P${first} が先行！`, type: 'info' }],

    // カードプレイ履歴（統計用）
    // 召喚/特殊召喚/魔法発動/フィールド・フェイズカード配置時に記録
    playedCardsHistory: {
      p1: [],
      p2: [],
    },
  };
}

// ========================================
// 状態アクセスヘルパー
// ========================================

/**
 * 指定プレイヤーの状態を取得
 */
export function getPlayer(state: GameState, player: PlayerNumber): PlayerState {
  return player === 1 ? state.p1 : state.p2;
}

/**
 * 相手プレイヤーの状態を取得
 */
export function getOpponent(state: GameState, player: PlayerNumber): PlayerState {
  return player === 1 ? state.p2 : state.p1;
}

/**
 * 現在のプレイヤー状態を取得
 */
export function getCurrentPlayer(state: GameState): PlayerState {
  return getPlayer(state, state.currentPlayer);
}

/**
 * 現在の相手プレイヤー状態を取得
 */
export function getCurrentOpponent(state: GameState): PlayerState {
  return getOpponent(state, state.currentPlayer);
}

/**
 * 相手プレイヤー番号を取得
 */
export function getOpponentNumber(player: PlayerNumber): PlayerNumber {
  return player === 1 ? 2 : 1;
}

// ========================================
// 状態更新ヘルパー（イミュータブル）
// ========================================

/**
 * プレイヤー状態を更新
 */
export function updatePlayer(state: GameState, player: PlayerNumber, updates: Partial<PlayerState>): GameState {
  const key = player === 1 ? 'p1' : 'p2';
  return {
    ...state,
    [key]: {
      ...state[key],
      ...updates,
    },
  };
}

/**
 * ログを追加
 */
export function addLog(state: GameState, message: string, type: LogEntry['type'] = 'info'): GameState {
  const newLogs: LogEntry[] = [...state.logs, { message, type }];
  // 最大100件保持
  if (newLogs.length > 100) {
    newLogs.shift();
  }
  return {
    ...state,
    logs: newLogs,
  };
}

/**
 * ターンフラグを更新
 */
export function updateTurnFlags(state: GameState, updates: Partial<TurnFlags>): GameState {
  return {
    ...state,
    turnFlags: {
      ...state.turnFlags,
      ...updates,
    },
  };
}

/** ゲーム進行状態の更新可能なフィールド */
export interface GameProgressUpdates {
  turn?: number;
  currentPlayer?: PlayerNumber;
  phase?: PhaseType;
  isFirstTurn?: boolean;
  winner?: PlayerNumber | null;
  gameStatus?: GameStatusType;
}

/**
 * ゲーム進行状態を更新
 */
export function updateGameProgress(state: GameState, updates: GameProgressUpdates): GameState {
  return {
    ...state,
    ...updates,
  };
}

// ========================================
// 判定ヘルパー
// ========================================

/**
 * ゲームが終了しているか
 */
export function isGameOver(state: GameState): boolean {
  return state.gameStatus === GAME_STATUS.GAME_OVER || state.winner !== null;
}

/**
 * 勝者を判定（ライフ0チェック）
 */
export function checkWinner(state: GameState): PlayerNumber | null {
  if (state.p1.life <= 0) return 2;
  if (state.p2.life <= 0) return 1;
  return null;
}

/** 表示・AI判断・支払いで使う、召喚直前の状態に対するコスト。 */
export function getSummonCost(
  state: GameState,
  card: CardWithId,
  summoner: PlayerNumber = state.currentPlayer,
): number {
  const player = getPlayer(state, summoner);
  return calculateSummonCost(
    card.cost,
    calculateSummonCostModifierPure(state, card, summoner),
    card.tempCostModifier ?? 0,
    statusEffectEngine.getSummonCostReduction(player.statusEffects, card.attribute),
  );
}

/**
 * カードが召喚可能か
 */
export function canSummonCard(state: GameState, card: CardWithId, slotIndex: number): boolean {
  const player = getCurrentPlayer(state);

  // モンスターカードのみ
  if (card.type !== 'monster') return false;

  // コスト軽減を考慮したSPチェック（activeSP + bonusSP - statusEffect軽減 - tempCostModifier）
  const totalAvailableSP = player.activeSP + (player.bonusSP || 0);
  const actualCost = getSummonCost(state, card);
  if (actualCost > totalAvailableSP) return false;

  // スロットが空いているか
  if (player.field[slotIndex] !== null) return false;

  // メインフェイズか
  if (state.phase !== PHASES.MAIN) return false;

  return true;
}

/**
 * 魔法カードが使用可能か
 */
export function canUseMagic(state: GameState, card: CardWithId): boolean {
  const player = getCurrentPlayer(state);

  // 魔法カードのみ
  if (card.type !== 'magic') return false;

  // SPチェック（activeSP + bonusSP）
  const totalAvailableSP = player.activeSP + (player.bonusSP || 0);
  if (card.cost > totalAvailableSP) return false;

  // 魔法ブロック中か
  if (player.magicBlocked) return false;

  // メインフェイズか
  if (state.phase !== PHASES.MAIN) return false;

  return true;
}

/**
 * モンスターが攻撃可能か
 */
export function canMonsterAttack(monster: MonsterInstance | null): boolean {
  if (!monster) return false;
  if (!monster.canAttack) return false;
  // 状態異常チェック（CAGED, FREEZE, STUN, SLEEP）
  if (!statusEffectEngine.canAttack(monster)) return false;
  return true;
}

/**
 * フィールドにモンスターがいるか
 */
export function hasMonsterOnField(field: (MonsterInstance | null)[]): boolean {
  return field.some(slot => slot !== null);
}

/**
 * フィールドの空きスロット数
 */
export function getEmptySlotCount(field: (MonsterInstance | null)[]): number {
  return field.filter(slot => slot === null).length;
}

/**
 * フィールドの最初の空きスロットインデックス
 * @returns スロットインデックス (-1 if full)
 */
export function getFirstEmptySlot(field: (MonsterInstance | null)[]): number {
  return field.findIndex(slot => slot === null);
}

// ========================================
// デバッグ用
// ========================================

/** プレイヤーサマリー */
export interface PlayerSummary {
  life: number;
  hand: number;
  deck: number;
  field: number;
  graveyard: number;
  sp: string;
}

/** ゲーム状態サマリー */
export interface StateSummary {
  turn: number;
  currentPlayer: PlayerNumber;
  phase: string;
  p1: PlayerSummary;
  p2: PlayerSummary;
  winner: PlayerNumber | null;
}

/**
 * ゲーム状態のサマリーを取得
 */
export function getStateSummary(state: GameState): StateSummary {
  return {
    turn: state.turn,
    currentPlayer: state.currentPlayer,
    phase: PHASE_NAMES[state.phase],
    p1: {
      life: state.p1.life,
      hand: state.p1.hand.length,
      deck: state.p1.deck.length,
      field: state.p1.field.filter(m => m !== null).length,
      graveyard: state.p1.graveyard.length,
      sp: `${state.p1.activeSP}/${state.p1.activeSP + state.p1.restedSP}`,
    },
    p2: {
      life: state.p2.life,
      hand: state.p2.hand.length,
      deck: state.p2.deck.length,
      field: state.p2.field.filter(m => m !== null).length,
      graveyard: state.p2.graveyard.length,
      sp: `${state.p2.activeSP}/${state.p2.activeSP + state.p2.restedSP}`,
    },
    winner: state.winner,
  };
}
